import { defineStore } from 'pinia'
import { ref } from 'vue'

/**
 * Alternative names for the PUBLIC availability search.
 *
 * The signed "pick another name" flow keeps its own copy of this in
 * BNChangeStore, bound to one order and one signed URL. This one answers the
 * other half of the question: somebody typing into the search box on the
 * marketing site, with no order behind them, who has just been told the name
 * they want is taken.
 *
 * Deliberately NOT persisted, unlike BNStore. An alternative is only true for as
 * long as nobody else registers it, so a list restored from localStorage days
 * later would be a confident lie. It also never surfaces an error: suggestions
 * are a bonus on top of the verdict, and a visitor who came to check a name must
 * never be shown a failure for something they did not ask for.
 *
 * The one rule the whole store exists to keep: whatever is on screen belongs to
 * the name in `forName`, always. A list that outlived its search is worse than
 * no list at all — every row carries a button that fills the form in.
 */
export const useBNSuggestionsStore = defineStore('bnsuggestions', () => {
  const alternatives = ref([])
  const loading = ref(false)
  const loadingMore = ref(false)
  const exhausted = ref(false)
  const nextOffset = ref(0)

  // The name the list on screen belongs to. Only ever set next to the rows it
  // describes, so the two cannot drift apart.
  const forName = ref('')

  // Every reply is matched against the search that asked for it: a slow first
  // page must never overwrite the results of a newer search.
  let seq = 0

  const clear = () => {
    alternatives.value = []
    exhausted.value = false
    nextOffset.value = 0
    forName.value = ''
  }

  const reset = () => {
    seq++
    loading.value = false
    loadingMore.value = false
    clear()
  }

  /** First page of alternatives for a name ASIC has just rejected. */
  const suggest = async (name) => {
    const value = (name ?? '').trim()

    if (!value) return

    // Same name, list already on screen: nothing to buy.
    if (value === forName.value && alternatives.value.length) return

    const mine = ++seq

    // Anything on screen belongs to a different name, so it goes now rather than
    // when the reply lands: a refusal for the new name must never be illustrated
    // with the old name's alternatives, and this call may fail or be throttled.
    clear()

    loading.value = true
    // A "show more" from the old list has been orphaned by the ++seq above; its
    // spinner would otherwise never stop.
    loadingMore.value = false

    try {
      const { data } = await axios.post('/api/bn/suggestions', { name: value, offset: 0 })

      if (mine !== seq) return

      alternatives.value = data.alternatives ?? []
      nextOffset.value = data.next_offset ?? 0
      exhausted.value = !!data.exhausted
      forName.value = value
    } catch {
      // Silent on purpose: the verdict is the answer they came for. Nothing is
      // left attributed to this name, so checking it again retries.
      if (mine !== seq) return

      clear()
    } finally {
      if (mine === seq) loading.value = false
    }
  }

  /** The next page, appended below the list already on screen. */
  const suggestMore = async () => {
    if (loading.value || loadingMore.value || exhausted.value || !forName.value) return

    const mine = seq
    const value = forName.value

    loadingMore.value = true

    try {
      const { data } = await axios.post('/api/bn/suggestions', { name: value, offset: nextOffset.value })

      // A fresh search started meanwhile: this page belongs to the old list.
      if (mine !== seq) return

      const seen = new Set(alternatives.value.map((a) => a.name))
      const fresh = (data.alternatives ?? []).filter((a) => !seen.has(a.name))

      alternatives.value = [...alternatives.value, ...fresh]
      nextOffset.value = data.next_offset ?? nextOffset.value
      exhausted.value = !!data.exhausted

      // A page that found nothing is the practical end of the list even when the
      // finder still has candidates: don't leave a button that keeps saying no.
      if (fresh.length === 0 && !data.degraded) exhausted.value = true
    } catch {
      // Nothing to show for it; the list on screen stays as it was. The button
      // comes back so they can try again.
    } finally {
      // Unconditional: a newer search already reset this flag, and leaving it
      // true here would kill paging for the rest of the visit.
      if (mine === seq) loadingMore.value = false
    }
  }

  /** The verified row behind a name the visitor picked, if we still have it. */
  const find = (name) => alternatives.value.find((a) => a.name === name) ?? null

  return {
    alternatives,
    exhausted,
    find,
    forName,
    loading,
    loadingMore,
    reset,
    suggest,
    suggestMore,
  }
})
